// 채점 대시보드(GradingDashboard)가 학생 답안을 문항에 맞추는 순수 함수 (계약: docs/시험_답안_채점_API.md 9절 「프론트 대응」).
// 답안은 questionId 로 문항을 찾는다 — 배열 순서·인덱스로 맞추지 않는다.
// 학생이 푼 버전(결과 응답 students[].version)의 스냅샷(exam.versions[버전].questions) 순서로 번호를 매기고 내용을 그린다.
// B형은 A형과 같은 문항·배점을 문항 단위로 섞은 것이라(docs/시험_관리_API.md 「시험 버전」, 2026-10-15 성취도 백엔드 회신)
// 같은 questionId 라도 버전마다 번호가 다르다. 문항별 분석은 questionId 로 모으고 번호·내용은 A형 기준으로 보인다.
// 2026-10-15 전에는 답안 배열의 인덱스로 번호를 매기고 A형 스냅샷의 같은 인덱스에서 내용·기능을 읽어, B형 학생은 번호·문제·선택지·말하기/쓰기 판정이 어긋났다.

export const questionIdOf = (item) => (item && (item.questionId || item.id || item._id)) || ''

// 문항의 기능(skill) — 소문자. 없으면 ''
export const skillOf = (question) => {
  const skill = question && (question.skill || question.category || '')
  return typeof skill === 'string' ? skill.toLowerCase() : ''
}

export const isManualGradingSkill = (skill) => skill === 'speaking' || skill === 'writing'

// 수동 채점 점수 상한 — 문항 배점(스냅샷 score). 배점이 없는 문항은 10점(전부터의 화면 기본값)
export const manualScoreMax = (question) => {
  const max = Number(question && question.score)
  return max > 0 ? max : 10
}

// 수동 채점 점수를 0 ~ 상한으로 맞춘다. 입력칸의 화살표는 min/max 로 막히지만 키보드로 친 값은 브라우저가 막지 않는다(2026-10-23).
// 숫자가 아닌 값(빈 칸 등)은 null
export const clampManualScore = (value, max) => {
  if (value === '' || value === null || value === undefined) return null
  const score = Number(value)
  if (Number.isNaN(score)) return null
  return Math.min(Math.max(score, 0), max)
}

// 스냅샷 문항 정규화 — 문자열(JSON)로 온 문항, 객체로 온 questionText 를 화면이 읽는 모양으로 (전부터 있던 보정)
export const normalizeSnapshotQuestion = (raw) => {
  let question = raw
  if (typeof question === 'string') {
    try {
      question = JSON.parse(question)
    } catch {
      return null
    }
  }
  if (!question || typeof question !== 'object') return null
  const text = question.questionText
  if (text && typeof text === 'object') {
    question = { ...question, questionText: text.text || text.content || text.html || '문제 내용 형식 오류' }
  }
  return question
}

const pickVersionSnapshot = (exam, key) => {
  const versions = exam && exam.versions && typeof exam.versions === 'object' ? exam.versions : {}
  const list = key && versions[key] && versions[key].questions
  return Array.isArray(list) && list.length > 0 ? list : null
}

// 실제로 그리는 스냅샷의 버전 키 — 그 버전이 없으면 'A', A형도 없으면 null(예전 형식 exam.questions).
// 학생의 응시 버전과 다른 스냅샷으로 그릴 때 화면이 「A형 기준」을 보이는 데 쓴다(관리자 화면과 같은 규칙, 2026-10-15)
export const resolveVersionKey = (exam, version) => {
  if (pickVersionSnapshot(exam, version)) return version
  if (pickVersionSnapshot(exam, 'A')) return 'A'
  return null
}

// 버전의 스냅샷 문항 목록. 그 버전이 없으면 A형, 그것도 없으면 예전 형식 exam.questions
export const getVersionQuestions = (exam, version) => {
  const key = resolveVersionKey(exam, version)
  const list = key ? pickVersionSnapshot(exam, key) : (Array.isArray(exam && exam.questions) ? exam.questions : [])
  return list.map(normalizeSnapshotQuestion).filter(Boolean)
}

// 시험이 가진 버전 키 중 A 가 아닌 것이 있는지 — 화면이 「A형 기준」 같은 안내를 보일지 정할 때
export const hasOtherVersions = (exam) => {
  const versions = exam && exam.versions && typeof exam.versions === 'object' ? exam.versions : {}
  return Object.keys(versions).some(key => key !== 'A' && versions[key])
}

// 기준(A형) 문항 번호표 — questionId → { question, number }. 문항별 분석·문제 상세의 번호와 내용
export const buildQuestionIndex = (exam) => {
  const byId = new Map()
  getVersionQuestions(exam, 'A').forEach((question, i) => {
    const id = questionIdOf(question)
    if (id && !byId.has(id)) byId.set(id, { question, number: i + 1 })
  })
  return byId
}

// 학생 한 명의 답안을 그 학생이 푼 버전의 순서로 — [{ number, questionId, question, answer, answerIndex, skill }].
// 답안이 없는 문항도 행으로 둔다(answer: null — 미응답). 버전 스냅샷에 없는 답안(문항 삭제 등)은 뒤에 이어 붙인다(question: null).
export const buildStudentAnswerRows = (exam, student) => {
  const answers = Array.isArray(student && student.answers) ? student.answers : []
  const answerById = new Map()
  answers.forEach((answer, index) => {
    const id = questionIdOf(answer)
    if (id && !answerById.has(id)) answerById.set(id, { answer, index })
  })
  const rows = []
  const used = new Set()
  getVersionQuestions(exam, student && student.version).forEach(question => {
    const id = questionIdOf(question)
    const hit = id ? answerById.get(id) : null
    if (hit) used.add(id)
    rows.push({
      number: rows.length + 1,
      questionId: id,
      question,
      answer: hit ? hit.answer : null,
      answerIndex: hit ? hit.index : -1,
      skill: skillOf(question)
    })
  })
  answers.forEach((answer, index) => {
    const id = questionIdOf(answer)
    if (id && used.has(id)) return
    if (id) used.add(id)
    rows.push({ number: rows.length + 1, questionId: id, question: null, answer, answerIndex: index, skill: '' })
  })
  return rows
}

const pointsOf = (answer) => Number(answer && (answer.score ?? answer.points)) || 0

// 문항별 분석 — questionId 로 모은다. 번호·내용·배점은 A형 기준이고, 학생마다 그 학생 버전의 번호(number)를 함께 둔다.
// A형 스냅샷의 모든 문항을 먼저 두므로 답안이 하나도 없는 문항도 목록에 있다(hasAnswers: false). 화면은 그런 문항의 정답률·평균 득점·난이도를
// 「-」로 보이고 막대·차트를 비운다 — 「응답 없음」과 「전원 오답(0%)」을 가르기 위해(2026-10-15 강사 화면 결정, 관리자 화면팀에 통지).
// A형 스냅샷에 없는 문항(삭제 등)은 A형 문항 수 다음 번호를 처음 나온 순서로 받는다.
export const aggregateQuestionStats = (exam, students) => {
  const index = buildQuestionIndex(exam)
  const stats = new Map()
  const ensure = (questionId) => {
    if (!stats.has(questionId)) {
      const known = index.get(questionId)
      stats.set(questionId, {
        id: questionId,
        questionId,
        number: known ? known.number : null,
        question: known ? known.question : null,
        maxPoints: known ? (Number(known.question.score ?? known.question.points) || 0) : 0,
        type: 'Question',
        total: 0,
        correct: 0,
        pointsSum: 0,
        students: []
      })
    }
    return stats.get(questionId)
  }
  index.forEach((_, questionId) => ensure(questionId))
  ;(Array.isArray(students) ? students : []).forEach(student => {
    buildStudentAnswerRows(exam, student).forEach(row => {
      if (!row.answer || !row.questionId) return
      const stat = ensure(row.questionId)
      const points = pointsOf(row.answer)
      stat.total++
      if (row.answer.isCorrect) stat.correct++
      stat.pointsSum += points
      stat.students.push({
        studentId: student.studentId,
        studentName: student.studentName,
        version: student.version || '',
        number: row.number,
        isCorrect: row.answer.isCorrect,
        points
      })
    })
  })
  const list = [...stats.values()]
  let nextNumber = index.size
  list.filter(stat => stat.number === null).forEach(stat => { stat.number = ++nextNumber })
  list.sort((a, b) => a.number - b.number)
  return list.map(stat => ({
    ...stat,
    hasAnswers: stat.total > 0,
    incorrect: stat.total - stat.correct,
    correctRate: stat.total > 0 ? Math.round((stat.correct / stat.total) * 100) : 0,
    averagePoints: stat.total > 0 ? Number((stat.pointsSum / stat.total).toFixed(1)) : 0
  }))
}
